import React, { useState, useEffect, useRef } from 'react';
import { getAllCategories, createPost, updatePost, BlogPost, BlogCategory, getAllTags, BlogTag } from '../../services/blogService';
import { generateBlogPost, enhanceBlogPost, generateSEO, suggestImprovements, BlogAIOptions } from '../../services/blogAIService';
import { useAuth } from '../../contexts/AuthContext';
import { RichTextEditor } from '../ui/RichTextEditor';
import './BlogPostEditor.scss';

interface BlogPostEditorProps {
  post: BlogPost | null;
  onSave: () => void;
  onCancel: () => void;
}

const BlogPostEditor: React.FC<BlogPostEditorProps> = ({ post, onSave, onCancel }) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState<BlogTag[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allTags, setAllTags] = useState<BlogTag[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [featuredImage, setFeaturedImage] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [published, setPublished] = useState(false);
  const [featured, setFeatured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAIOptions, setShowAIOptions] = useState(false);
  const [aiTopic, setAITopic] = useState('');
  const [aiKeywords, setAIKeywords] = useState('');
  const [aiTone, setAITone] = useState<BlogAIOptions['tone']>('informative');
  const [aiLength, setAILength] = useState<BlogAIOptions['length']>('medium');
  const [aiPurpose, setAIPurpose] = useState<BlogAIOptions['purpose']>('inform');
  const [aiAudienceLevel, setAIAudienceLevel] = useState<BlogAIOptions['audienceLevel']>('intermediate');
  const [aiIncludeSEO, setAIIncludeSEO] = useState(true);
  const [aiIncludeCTA, setAIIncludeCTA] = useState(true);
  const [aiIncludeAppBenefits, setAIIncludeAppBenefits] = useState(true);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [showImprovementSuggestions, setShowImprovementSuggestions] = useState(false);
  const [improvementSuggestions, setImprovementSuggestions] = useState<string[]>([]);
  const [enhancementInstructions, setEnhancementInstructions] = useState('');
  const [enhancing, setEnhancing] = useState(false);
  const [showGenerateSEO, setShowGenerateSEO] = useState(false);
  const [generatingSEO, setGeneratingSEO] = useState(false);
  const editorRef = useRef<any>(null);

  // Check if we have the Gemini API key
  const [hasApiKey, setHasApiKey] = useState(true);
  
  // Check for API key on component mount
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) {
      setHasApiKey(false);
      console.error("Missing Gemini API key");
    }
  }, []);

  // Fetch categories and tags, initialize form if editing
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesData, tagsData] = await Promise.all([
          getAllCategories(),
          getAllTags()
        ]);
        setCategories(categoriesData);
        setAllTags(tagsData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load categories and tags');
      }
    };

    fetchData();

    // If editing, initialize form with post data
    if (post) {
      setTitle(post.title);
      setSummary(post.summary);
      setContent(post.content);
      setCategoryId(post.category_id || '');
      setTags(post.tags ? post.tags.map(tag => tag.name) : []);
      setFeaturedImage(post.featured_image || '');
      setMetaTitle(post.meta_title || '');
      setMetaDescription(post.meta_description || '');
      setPublished(post.published);
      setFeatured(post.featured);
    }
  }, [post]);

  // Update suggestions when tag input changes
  useEffect(() => {
    if (tagInput.trim()) {
      const input = tagInput.toLowerCase();
      const suggestions = allTags
        .filter(tag => 
          tag.name.toLowerCase().includes(input) && 
          !tags.includes(tag.name)
        )
        .slice(0, 10); // Limit to 10 suggestions
      setTagSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      setSelectedSuggestionIndex(-1);
    } else {
      setTagSuggestions([]);
      setShowSuggestions(false);
    }
  }, [tagInput, tags, allTags]);

  // Handle tag input change
  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  };

  // Handle tag selection from suggestions
  const handleSelectTag = (tagName: string) => {
    if (!tags.includes(tagName)) {
      setTags([...tags, tagName]);
    }
    setTagInput('');
    setShowSuggestions(false);
  };

  // Handle keyboard navigation in suggestions
  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < tagSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          handleSelectTag(tagSuggestions[selectedSuggestionIndex].name);
        } else if (tagInput.trim()) {
          handleAddTag();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  // Handle tag addition
  const handleAddTag = () => {
    if (!tagInput.trim()) return;

    // Don't add duplicate tags
    if (!tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
    }

    setTagInput('');
    setShowSuggestions(false);
  };

  // Handle tag removal
  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent, saveAsDraft: boolean = false) => {
    e.preventDefault();

    if (!title.trim() || !summary.trim() || !content.trim()) {
      setError('Title, summary, and content are required');
      return;
    }

    setSaving(true);
    setError(null);

    const postData = {
      title,
      summary,
      content,
      category_id: categoryId || undefined,
      featured_image: featuredImage || undefined,
      tags,
      published: saveAsDraft ? false : published,
      featured,
      meta_title: metaTitle || undefined,
      meta_description: metaDescription || undefined
    };

    try {
      let result;

      if (post) {
        // Update existing post
        result = await updatePost(post.id, postData);
      } else {
        // Create new post
        if (!user?.id) {
          throw new Error('User not authenticated');
        }
        result = await createPost(postData, user.id);
      }

      if (result) {
        setSuccess('Post saved successfully');
        // Notify parent component
        onSave();
      } else {
        setError('Failed to save post');
      }
    } catch (err) {
      console.error('Error saving post:', err);
      setError('An error occurred while saving the post');
    }

    setSaving(false);
  };

  // Handle AI-generated content
  const handleGenerateContent = async () => {
    if (!aiTopic.trim()) {
      setError('Please provide a topic for AI-generated content');
      return;
    }

    setGeneratingContent(true);
    setError(null);

    try {
      const options: BlogAIOptions = {
        topic: aiTopic,
        keywords: aiKeywords.split(',').map(kw => kw.trim()).filter(kw => kw),
        tone: aiTone,
        length: aiLength,
        purpose: aiPurpose,
        audienceLevel: aiAudienceLevel,
        includeSEO: aiIncludeSEO,
        includeCTA: aiIncludeCTA,
        includeAppBenefits: aiIncludeAppBenefits
      };

      const generatedContent = await generateBlogPost(options);

      if (generatedContent) {
        setTitle(generatedContent.title || '');
        setSummary(generatedContent.summary || '');
        setContent(generatedContent.content || '');
        
        // Hide AI options panel
        setShowAIOptions(false);
        
        setSuccess('Content generated successfully');
      } else {
        setError('Failed to generate content');
      }
    } catch (err) {
      console.error('Error generating content:', err);
      setError('An error occurred while generating content');
    }

    setGeneratingContent(false);
  };

  // Handle AI enhancement of existing content
  const handleEnhanceContent = async () => {
    if (!enhancementInstructions.trim()) {
      setError('Please provide instructions for enhancing the content');
      return;
    }

    setEnhancing(true);
    setError(null);

    try {
      const enhancedContent = await enhanceBlogPost(content, enhancementInstructions);

      if (enhancedContent) {
        setContent(enhancedContent);
        setSuccess('Content enhanced successfully');
      } else {
        setError('Failed to enhance content');
      }
    } catch (err) {
      console.error('Error enhancing content:', err);
      setError('An error occurred while enhancing content');
    }

    setEnhancing(false);
  };

  // Get AI suggestions for improving the content
  const handleGetSuggestions = async () => {
    if (!content.trim()) {
      setError('Please write some content first');
      return;
    }

    setShowImprovementSuggestions(true);
    setImprovementSuggestions([]);
    setError(null);

    try {
      const suggestions = await suggestImprovements(content);

      if (suggestions) {
        setImprovementSuggestions(suggestions);
      } else {
        setError('Failed to get improvement suggestions');
      }
    } catch (err) {
      console.error('Error getting suggestions:', err);
      setError('An error occurred while getting suggestions');
    }
  };

  // Generate SEO metadata
  const handleGenerateSEO = async () => {
    if (!content.trim()) {
      setError('Please write some content first');
      return;
    }

    setGeneratingSEO(true);
    setError(null);

    try {
      const seoData = await generateSEO(content);

      if (seoData) {
        setMetaTitle(seoData.title);
        setMetaDescription(seoData.description);
        setShowGenerateSEO(false);
        setSuccess('SEO metadata generated successfully');
      } else {
        setError('Failed to generate SEO metadata');
      }
    } catch (err) {
      console.error('Error generating SEO:', err);
      setError('An error occurred while generating SEO metadata');
    }

    setGeneratingSEO(false);
  };

  return (
    <div className="blog-post-editor">
      <div className="editor-header">
        <h2>{post ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
        <div className="editor-actions">
          <button 
            className="ai-assist-button" 
            onClick={() => setShowAIOptions(true)}
            type="button"
            disabled={!hasApiKey}
            title={!hasApiKey ? "API key missing" : "Get AI assistance with your blog post"}
          >
            <i className="fas fa-robot"></i> AI Assist
          </button>
        </div>
      </div>
      
      {!hasApiKey && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle"></i>
          <span>AI features are disabled because the Gemini API key is missing. Please check your environment configuration.</span>
        </div>
      )}
      
      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button 
            className="close-button" 
            onClick={() => setError(null)}
            type="button"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          <span>{success}</span>
          <button 
            className="close-button" 
            onClick={() => setSuccess(null)}
            type="button"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      
      {/* Main form content */}
      <form className="post-form" onSubmit={(e) => handleSubmit(e)}>
        <div className="form-row">
          <div className="form-group title-group">
            <label htmlFor="post-title">Title</label>
            <input
              id="post-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter post title"
              required
            />
          </div>
          
          <div className="form-group category-group">
            <label htmlFor="post-category">Category</label>
            <select
              id="post-category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="post-summary">Summary</label>
          <textarea
            id="post-summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Write a brief summary of the post (2-3 sentences)"
            rows={2}
            required
          ></textarea>
        </div>

        <div className="form-group">
          <label htmlFor="post-content">Content</label>
          <RichTextEditor
            ref={editorRef}
            initialValue={content}
            onChange={setContent}
          />
          
          <div className="editor-tools">
            <button 
              type="button"
              className="tool-button"
              onClick={handleGetSuggestions}
            >
              <i className="fas fa-lightbulb"></i>
              Get Improvement Suggestions
            </button>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group tags-group">
            <label htmlFor="post-tags">Tags</label>
            <div className="tags-input-container">
              <div className="tags-input-wrapper">
                <input
                  id="post-tags"
                  type="text"
                  value={tagInput}
                  onChange={handleTagInputChange}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Add a tag and press Enter"
                />
                {showSuggestions && (
                  <div className="tag-suggestions">
                    {tagSuggestions.map((tag, index) => (
                      <div
                        key={tag.id}
                        className={`tag-suggestion ${index === selectedSuggestionIndex ? 'selected' : ''}`}
                        onClick={() => handleSelectTag(tag.name)}
                      >
                        <span className="tag-name">{tag.name}</span>
                        <span className="post-count">({tag.post_count || 0} posts)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="add-tag-button"
                onClick={handleAddTag}
              >
                <i className="fas fa-plus"></i>
              </button>
            </div>
            
            {tags.length > 0 && (
              <div className="tags-list">
                {tags.map(tag => (
                  <div key={tag} className="tag">
                    {tag}
                    <button
                      type="button"
                      className="remove-tag-button"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="form-group featured-image-group">
            <label htmlFor="post-featured-image">Featured Image URL</label>
            <input
              id="post-featured-image"
              type="text"
              value={featuredImage}
              onChange={(e) => setFeaturedImage(e.target.value)}
              placeholder="Enter image URL"
            />
          </div>
        </div>

        <div className="form-section seo-section">
          <div className="section-header">
            <h3>SEO Settings</h3>
            <button
              type="button"
              className="generate-seo-button"
              onClick={() => setShowGenerateSEO(true)}
            >
              <i className="fas fa-magic"></i>
              Generate SEO
            </button>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="post-meta-title">Meta Title (optional)</label>
              <input
                id="post-meta-title"
                type="text"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                placeholder="SEO-friendly title"
                maxLength={60}
              />
              <div className="character-count">
                {metaTitle.length}/60 characters
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="post-meta-description">Meta Description (optional)</label>
              <textarea
                id="post-meta-description"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                placeholder="Brief description for search results"
                rows={2}
                maxLength={155}
              ></textarea>
              <div className="character-count">
                {metaDescription.length}/155 characters
              </div>
            </div>
          </div>
        </div>

        <div className="form-section publish-section">
          <div className="section-header">
            <h3>Publishing Options</h3>
          </div>
          
          <div className="checkbox-group">
            <div className="checkbox-item">
              <input
                id="post-published"
                type="checkbox"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
              />
              <label htmlFor="post-published">Publish immediately</label>
            </div>
            
            <div className="checkbox-item">
              <input
                id="post-featured"
                type="checkbox"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
              />
              <label htmlFor="post-featured">Featured post</label>
            </div>
          </div>
        </div>

        {/* Form actions - these will be sticky at the bottom */}
        <div className="form-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={onCancel}
            disabled={saving || loading || generatingContent || enhancing || generatingSEO}
          >
            Cancel
          </button>
          <button
            type="button"
            className="save-draft-button"
            onClick={(e) => handleSubmit(e, true)}
            disabled={saving || loading || generatingContent || enhancing || generatingSEO}
          >
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            type="submit"
            className="publish-button"
            disabled={saving || loading || generatingContent || enhancing || generatingSEO}
          >
            {saving ? 'Saving...' : post ? 'Update Post' : 'Publish Post'}
          </button>
        </div>
      </form>
      
      {/* AI options modal */}
      {showAIOptions && (
        <div className="ai-options-panel">
          <div className="panel-content">
            <div className="panel-header">
              <h3>Generate Blog Post with AI</h3>
              <button 
                className="close-button"
                onClick={() => setShowAIOptions(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="ai-options-form">
              <div className="form-group">
                <label htmlFor="ai-topic">Topic</label>
                <input
                  id="ai-topic"
                  type="text"
                  value={aiTopic}
                  onChange={(e) => setAITopic(e.target.value)}
                  placeholder="What should the post be about?"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="ai-keywords">Keywords (comma separated)</label>
                <input
                  id="ai-keywords"
                  type="text"
                  value={aiKeywords}
                  onChange={(e) => setAIKeywords(e.target.value)}
                  placeholder="E.g., PLAB2, clinical skills, exam preparation"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ai-tone">Tone</label>
                  <select
                    id="ai-tone"
                    value={aiTone || 'informative'}
                    onChange={(e) => setAITone(e.target.value as BlogAIOptions['tone'])}
                  >
                    <option value="professional">Professional</option>
                    <option value="casual">Casual</option>
                    <option value="enthusiastic">Enthusiastic</option>
                    <option value="informative">Informative</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="ai-length">Length</label>
                  <select
                    id="ai-length"
                    value={aiLength || 'medium'}
                    onChange={(e) => setAILength(e.target.value as BlogAIOptions['length'])}
                  >
                    <option value="short">Short (300-500 words)</option>
                    <option value="medium">Medium (800-1200 words)</option>
                    <option value="long">Long (1500-2000 words)</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ai-purpose">Purpose</label>
                  <select
                    id="ai-purpose"
                    value={aiPurpose || 'inform'}
                    onChange={(e) => setAIPurpose(e.target.value as BlogAIOptions['purpose'])}
                  >
                    <option value="educate">Educate</option>
                    <option value="promote">Promote</option>
                    <option value="entertain">Entertain</option>
                    <option value="inform">Inform</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="ai-audience">Audience Level</label>
                  <select
                    id="ai-audience"
                    value={aiAudienceLevel || 'intermediate'}
                    onChange={(e) => setAIAudienceLevel(e.target.value as BlogAIOptions['audienceLevel'])}
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>

              <div className="checkbox-group">
                <div className="checkbox-item">
                  <input
                    id="ai-include-seo"
                    type="checkbox"
                    checked={aiIncludeSEO}
                    onChange={(e) => setAIIncludeSEO(e.target.checked)}
                  />
                  <label htmlFor="ai-include-seo">Optimize for SEO</label>
                </div>

                <div className="checkbox-item">
                  <input
                    id="ai-include-cta"
                    type="checkbox"
                    checked={aiIncludeCTA}
                    onChange={(e) => setAIIncludeCTA(e.target.checked)}
                  />
                  <label htmlFor="ai-include-cta">Include Call-to-Action</label>
                </div>

                <div className="checkbox-item">
                  <input
                    id="ai-include-app-benefits"
                    type="checkbox"
                    checked={aiIncludeAppBenefits}
                    onChange={(e) => setAIIncludeAppBenefits(e.target.checked)}
                  />
                  <label htmlFor="ai-include-app-benefits">Mention App Benefits</label>
                </div>
              </div>

              <div className="form-buttons">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowAIOptions(false)}
                  disabled={generatingContent}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="generate-button"
                  onClick={handleGenerateContent}
                  disabled={generatingContent}
                >
                  {generatingContent ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magic"></i>
                      Generate Content
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Content Enhancement Panel */}
      {showImprovementSuggestions && (
        <div className="ai-options-panel">
          <div className="panel-content">
            <div className="panel-header">
              <h3>Improvement Suggestions</h3>
              <button 
                className="close-button"
                onClick={() => setShowImprovementSuggestions(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="improvement-suggestions">
              {improvementSuggestions.length === 0 ? (
                <div className="loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  Analyzing content...
                </div>
              ) : (
                <>
                  <ul className="suggestions-list">
                    {improvementSuggestions.map((suggestion, index) => (
                      <li key={index}>
                        <i className="fas fa-lightbulb"></i>
                        {suggestion}
                      </li>
                    ))}
                  </ul>

                  <div className="enhancement-form">
                    <div className="form-group">
                      <label htmlFor="enhancement-instructions">Apply Improvements</label>
                      <textarea
                        id="enhancement-instructions"
                        value={enhancementInstructions}
                        onChange={(e) => setEnhancementInstructions(e.target.value)}
                        placeholder="Describe which improvements you'd like the AI to make..."
                        rows={3}
                      ></textarea>
                    </div>

                    <div className="form-buttons">
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={() => setShowImprovementSuggestions(false)}
                        disabled={enhancing}
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        className="enhance-button"
                        onClick={handleEnhanceContent}
                        disabled={enhancing || !enhancementInstructions.trim()}
                      >
                        {enhancing ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Enhancing...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-wand-magic-sparkles"></i>
                            Enhance Content
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SEO Generation Panel */}
      {showGenerateSEO && (
        <div className="ai-options-panel">
          <div className="panel-content">
            <div className="panel-header">
              <h3>Generate SEO Metadata</h3>
              <button 
                className="close-button"
                onClick={() => setShowGenerateSEO(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="seo-generation">
              <p>
                AI will analyze your content and generate an optimized title and meta description for search engines.
              </p>

              <div className="form-buttons">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowGenerateSEO(false)}
                  disabled={generatingSEO}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="generate-button"
                  onClick={handleGenerateSEO}
                  disabled={generatingSEO}
                >
                  {generatingSEO ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search"></i>
                      Generate SEO
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPostEditor; 